import Link from 'next/link';
import emojiFlags from 'emoji-flags';
import { nameFromCC } from 'tf-post-parser';
import FutureImage from '../FutureImage';
import { fraunces } from '../Landing/fonts';

function flagSrc(countryCode) {
  if (!countryCode || !emojiFlags.countryCode(countryCode.toUpperCase()))
    return null;
  return `/flags/${countryCode.toLowerCase()}.svg`;
}

function formatDate(createdAt) {
  // Pinned to UTC: cards are SSR'd on /blog, so server and client must
  // format identical strings or hydration mismatches.
  return new Date(createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// No byline: on /blog and on author profiles every card shares one author.
// Meta is date + read time; the location lives on the photo, where it can't
// read as the author's nationality.
export default function BlogPostCard({ post }) {
  const flag = flagSrc(post.location ? post.location.countryCode : undefined);
  const countryName =
    post.location && post.location.countryCode
      ? nameFromCC(post.location.countryCode)
      : undefined;
  const place = post.location
    ? post.location.city || post.location.subdivision
    : undefined;

  // Normalized /blog index cards carry href; profile-tab cards don't.
  const href = post.href || `/@${post.author.username}/${post.permlink}`;

  return (
    <Link
      href={href}
      prefetch={false}
      className="tw-group tw-block tw-no-underline"
    >
      <article className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-shadow-[0_8px_24px_-12px_rgba(29,45,40,0.3)] tw-transition-all tw-duration-300 group-hover:tw-shadow-[0_20px_40px_-12px_rgba(29,45,40,0.45)] group-hover:-tw-translate-y-1">
        {/* Photo */}
        <div className="tw-relative tw-isolate tw-aspect-[4/3] tw-overflow-hidden">
          {post.thumb ? (
            <FutureImage
              src={post.thumb.url}
              alt=""
              fill
              sizes="(max-width: 576px) 100vw, (max-width: 992px) 50vw, 33vw"
              maxSrcSetWidth={1200}
              placeholder={post.thumb.blur ? 'blur' : undefined}
              blurDataURL={post.thumb.blur}
              className="tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-[1.04]"
            />
          ) : (
            <div className="tw-h-full tw-w-full tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          )}
          {(flag || place) && (
            <span
              title={countryName}
              className="tw-absolute tw-left-2.5 tw-top-2.5 tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/90 tw-px-2 tw-py-0.5 tw-shadow-sm tw-backdrop-blur-sm"
            >
              {flag && (
                <img
                  src={flag}
                  alt=""
                  aria-hidden="true"
                  width={13}
                  height={13}
                  loading="lazy"
                  className="tw-h-[13px] tw-w-[13px]"
                />
              )}
              {place && (
                <span className="tw-max-w-[10rem] tw-truncate tw-text-[11px] tw-font-semibold tw-text-[#1D2D28]">
                  {place}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Content — teal left-bar slides in on hover */}
        <div className="tw-relative tw-p-4 tw-pl-5 before:tw-absolute before:tw-left-0 before:tw-top-4 before:tw-bottom-4 before:tw-w-[3px] before:tw-rounded-full before:tw-bg-primary-500 before:tw-opacity-0 before:tw-transition-opacity before:tw-duration-300 group-hover:before:tw-opacity-100">
          <p
            className={`${fraunces.className} tw-mb-0 tw-overflow-hidden tw-text-[17px] tw-font-semibold tw-leading-snug tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]`}
          >
            {post.title}
          </p>
          <p className="tw-mb-0 tw-mt-2 tw-font-mono tw-text-[11px] tw-tracking-wide tw-text-[#8A958F]">
            {post.category === 'guide' ? 'Guide · ' : ''}
            {formatDate(post.createdAt)}
            {post.readTime
              ? ` · ${Math.max(1, Math.round(post.readTime))} min read`
              : ''}
          </p>
        </div>
      </article>
    </Link>
  );
}
